import React, { useEffect, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import {
  enablePushNotifications,
  isPushSupported,
  readPushPermission,
  syncPushSubscription,
} from '../../utils/pushNotifications';
import {
  dismissPushPrompt,
  isPushPromptDismissed,
  notifyPushEnabled,
  subscribePushEnabled,
  subscribePushPrompt,
} from '../../utils/pushPrompt';

const shouldOpenOnEntry = (audience) => Boolean(
  isPushSupported()
  && readPushPermission() === 'default'
  && !isPushPromptDismissed(audience)
);

const PushNotificationPrompt = ({ audience }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => shouldOpenOnEntry(audience));
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('ready');

  useEffect(() => {
    if (!isPushSupported() || readPushPermission() !== 'granted') {
      return undefined;
    }

    syncPushSubscription(audience).catch(() => undefined);
    return undefined;
  }, [audience]);

  useEffect(() => subscribePushPrompt((requestedAudience) => {
    if (requestedAudience === audience) {
      setStatus('ready');
      setOpen(true);
    }
  }), [audience]);

  useEffect(() => subscribePushEnabled((enabledAudience) => {
    if (enabledAudience === audience) {
      setOpen(false);
    }
  }), [audience]);

  const hintKey = audience === 'customer'
    ? 'pushNotifications.customerHint'
    : 'pushNotifications.staffHint';
  const statusMessage = status === 'ready' ? null : t(`pushNotifications.${status}`);

  const close = () => {
    if (busy) {
      return;
    }
    dismissPushPrompt(audience);
    setOpen(false);
  };

  const activate = async () => {
    setBusy(true);
    setStatus('ready');
    try {
      const result = await enablePushNotifications(audience);
      if (result === 'enabled') {
        dismissPushPrompt(audience);
        notifyPushEnabled(audience);
        setOpen(false);
        return;
      }
      if (result !== 'dismissed') {
        setStatus(result);
      }
    } catch (error) {
      console.error('Failed to enable push notifications', error);
      setStatus('error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={close}
      maxWidth="sm"
      showCloseButton
      dividers
      title={t('pushNotifications.promptTitle')}
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={close} color="inherit" disabled={busy}>
            {t('pushNotifications.notNow')}
          </Button>
          <PrimaryActionButton onClick={activate} disabled={busy}>
            {t('pushNotifications.enable')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Typography variant="body1">
        {t(hintKey)}
      </Typography>
      {statusMessage ? (
        <Alert severity={status === 'error' ? 'error' : 'info'} sx={{ mt: 2 }}>
          {statusMessage}
        </Alert>
      ) : null}
    </ScrollableDialog>
  );
};

export default PushNotificationPrompt;
